import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  KeyRound, Loader2, CheckCircle2, AlertOctagon, Trash2,
  Eye, EyeOff, RefreshCw, Radar, ExternalLink, PlugZap, Globe2, FolderKanban,
  History, Search, Sparkles,
} from "lucide-react";

/** Woorank score → colour. Rules: 70+ green, 50-69 orange, <50 red. */
function scoreColor(score) {
  if (score == null) return "#94a3b8";
  if (score >= 70) return "#0f9d58";
  if (score >= 50) return "#f97316";
  return "#db4437";
}

/** Catalog of every Wordfence—err, Woorank—API endpoint documented at
 * apidocs.woorank.com. `implemented: true` means we pull it live; the
 * rest are wishlist / roadmap items. Kept as a static const because
 * the Woorank API surface is stable and small — no need to fetch. */
const WOORANK_ENDPOINTS = [
  { group: "Reviews", method: "GET", path: "/reviews",
    label: "Get review data", implemented: true,
    description: "Full on-page + off-page SEO review — score, section grades, criteria list, traffic estimate, Core Web Vitals. Powers the per-client Fetch latest button." },
  { group: "Reviews", method: "POST", path: "/reviews",
    label: "Create / refresh review", implemented: true,
    description: "Trigger a new Woorank crawl for a URL. Async — completes in ~30-60s via webhook. Powers the per-client Refresh crawl button." },
  { group: "Reviews", method: "POST", path: "/reviews/pdf",
    label: "Download white-label PDF", implemented: true,
    description: "Download the branded Woorank PDF report for a URL. Powers the per-client Download PDF button — attach to monthly client reports." },
  { group: "Metrics", method: "GET", path: "/metrics",
    label: "Scoring history", implemented: true,
    description: "Fetch Woorank's pre-computed scoring history for a URL. Cached into woorank_metrics — a supplement to the history we build from each Fetch latest." },
  { group: "Projects", method: "GET", path: "/projects",
    label: "List projects", implemented: true,
    description: "List every Woorank project on the account (folders of URLs). Shown in the Projects card below." },
  { group: "Projects", method: "POST", path: "/projects",
    label: "Create project", implemented: true,
    description: "Create a Woorank project pointing at a URL. Some Woorank plans require a project before /metrics or /keywords return data." },
  { group: "Keywords", method: "GET", path: "/keywords",
    label: "Keyword tracking", implemented: true,
    description: "Track ranking positions for target keywords per URL — optionally per-country. Powers the per-client Keywords panel." },
  { group: "CWV", method: "GET", path: "/cwv",
    label: "Core Web Vitals feed", implemented: true,
    description: "Standalone Core Web Vitals endpoint with histograms and INP — richer than what /reviews returns. Powers the per-client CWV panel." },
];

/**
 * Woorank Master — single-page master-settings tab for the agency-wide
 * Woorank API key. Mirrors the PSI / SendGrid Master layout: key entry
 * ↑ status card ↕ diagnostics ↓.
 *
 * Woorank auth uses the ``X-API-KEY`` header — never appears in URLs
 * or client-side code. The key is Fernet-encrypted at rest.
 */
export default function WoorankMasterPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyDraft, setKeyDraft] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [projects, setProjects] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [quickUrl, setQuickUrl] = useState("");
  const [quickBusy, setQuickBusy] = useState(false);
  const [extraDomain, setExtraDomain] = useState("");
  const [extraBusy, setExtraBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/woorank/master");
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Woorank state");
    } finally { setLoading(false); }
  }, []);

  const loadDomains = useCallback(async () => {
    setLoadingDomains(true);
    try {
      const { data } = await api.get("/woorank/domains-summary");
      setDomains(data.domains || []);
    } catch (e) { logger.debug("woorank domains-summary failed", e); /* table simply shows empty */ }
    finally { setLoadingDomains(false); }
  }, []);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const { data } = await api.get("/woorank/projects");
      setProjects(data.projects || []);
    } catch (e) {
      if (e.response?.status !== 400) {
        toast.error(e.response?.data?.detail || "Failed to load Woorank projects");
      }
      setProjects(null);
    } finally { setLoadingProjects(false); }
  }, []);

  const loadReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const { data } = await api.get("/woorank/reviews-list");
      setReviews(data.reviews || []);
    } catch (e) {
      if (e.response?.status !== 400) {
        toast.error(e.response?.data?.detail || "Failed to load audits");
      }
    } finally { setLoadingReviews(false); }
  }, []);

  const runBackfill = async () => {
    if (!window.confirm("Backfill will call Woorank's /metrics endpoint for every known URL and import the last 30 days of historical scores as synthetic history rows. Existing dates are skipped (idempotent). Proceed?")) return;
    setBackfilling(true);
    toast.info("Backfilling — this can take up to a minute…");
    try {
      const { data } = await api.post("/woorank/backfill-history");
      toast.success(`Imported ${data.imported} historical data-point(s) across ${data.urls_processed} URL(s)`);
      await Promise.all([loadReviews(), loadDomains(), loadProjects()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  const expandCompetitors = async () => {
    if (!window.confirm("Auto-expand will fetch a Woorank review for every competitor URL Woorank has listed on your projects. Idempotent (skips URLs already fetched today). Proceed?")) return;
    setExpanding(true);
    toast.info("Fetching competitor audits — up to a minute…");
    try {
      const { data } = await api.post("/woorank/expand-competitors");
      toast.success(`Imported ${data.imported} competitor audit(s) (${data.candidates} candidates)`);
      await loadReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Expand failed");
    } finally { setExpanding(false); }
  };

  const quickAudit = async () => {
    if (!quickUrl.trim()) return;
    setQuickBusy(true);
    try {
      const { data } = await api.post("/woorank/quick-audit",
        { url: quickUrl.trim() });
      toast.success(`Audited · score ${data.test?.score ?? "—"} for ${quickUrl}`);
      setQuickUrl("");
      await loadReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quick audit failed");
    } finally { setQuickBusy(false); }
  };

  const addExtraDomain = async () => {
    if (!extraDomain.trim()) return;
    setExtraBusy(true);
    try {
      await api.post("/woorank/extra-domains", { domain: extraDomain.trim() });
      toast.success(`Registered "${extraDomain.trim()}" — reloading projects…`);
      setExtraDomain("");
      await loadProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to register domain");
    } finally { setExtraBusy(false); }
  };

  useEffect(() => {
    load(); loadDomains();
    // Only attempt Woorank-authenticated calls after we know the key
    // is configured — otherwise the banner + 400 spam the console.
  }, [load, loadDomains]);
  useEffect(() => {
    if (state?.configured) { loadProjects(); loadReviews(); }
  }, [state?.configured, loadProjects, loadReviews]);

  const saveKey = async () => {
    if (!keyDraft.trim()) { toast.error("Paste an API key first"); return; }
    setBusy("save");
    try {
      const { data } = await api.put("/woorank/master", { api_key: keyDraft.trim() });
      toast.success("Woorank key saved (encrypted at rest)");
      setKeyDraft(""); setShowKey(false);
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the Woorank master API key? All Woorank SEO fetching will stop until a new key is entered.")) return;
    setBusy("clear");
    try {
      await api.delete("/woorank/master");
      toast.success("Cleared");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(null); }
  };

  const verify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.post("/woorank/master/verify");
      if (data.ok) {
        toast.success(`Woorank: ${data.detail}`);
      } else {
        toast.error(`Woorank rejected: ${data.detail}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setBusy(null); }
  };

  const configured = state?.configured;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="woorank-master-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#00A9E0]/10 grid place-items-center">
          <Radar className="h-5 w-5 text-[#00A9E0]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            Woorank Master
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Agency-wide Woorank SEO API key. Powers the per-client Woorank
            tab (SEO score, section grades, issues to fix) and the monthly
            scheduled rollup that appends new data-points per property URL.
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">API key</div>
            {loading ? (
              <Badge variant="outline"
                className="ml-2 rounded-sm text-[10px] font-mono">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> loading
              </Badge>
            ) : configured ? (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-emerald-100 text-emerald-800 border-emerald-200"
                data-testid="woorank-master-status-configured">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Configured
                {state?.key_hint && (
                  <span className="ml-1 opacity-70">· {state.key_hint}</span>
                )}
              </Badge>
            ) : (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-amber-100 text-amber-800 border-amber-200"
                data-testid="woorank-master-status-missing">
                <AlertOctagon className="h-3 w-3 mr-1" /> Not configured
              </Badge>
            )}
          </div>

          <div>
            <Label className="label-overline">
              {configured ? "Replace / rotate key" : "Paste your Woorank API key"}
            </Label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)}
                  placeholder="Paste the API key sent to you by commercial@woorank.com"
                  className="rounded-sm font-mono text-[12px] pr-8"
                  data-testid="woorank-master-key-input" />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <Button onClick={saveKey} disabled={busy !== null || !keyDraft.trim()}
                className="rounded-sm bg-[#00A9E0] hover:bg-[#0089B8] text-white"
                data-testid="woorank-master-save-btn">
                {busy === "save"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : "Save"}
              </Button>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-1.5 leading-relaxed">
              API keys are issued directly by Woorank —{" "}
              <a href="https://apidocs.woorank.com/"
                target="_blank" rel="noreferrer"
                className="text-[#00A9E0] hover:underline inline-flex items-center gap-0.5">
                see the API docs
                <ExternalLink className="h-2.5 w-2.5" />
              </a>{" "}
              or email <span className="text-zinc-700">commercial@woorank.com</span>.
              Rate limit is 1,000 calls/hour by default. Key is Fernet-encrypted
              at rest and only ever sent to Woorank in the ``X-API-KEY``
              header — never appears in URLs, logs, or browser code.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
            <Button size="sm" variant="outline" onClick={verify}
              disabled={busy !== null || !configured}
              className="rounded-sm"
              data-testid="woorank-master-verify-btn">
              {busy === "verify"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Test connection
            </Button>
            <Button size="sm" variant="outline" onClick={clearKey}
              disabled={busy !== null || !configured}
              className="rounded-sm text-red-600 hover:bg-red-50"
              data-testid="woorank-master-clear-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear key
            </Button>
            {state?.last_verified_at && (
              <span className="ml-auto text-[10px] font-mono text-zinc-500">
                {state.last_verified_ok ? (
                  <span className="text-emerald-700">
                    <CheckCircle2 className="inline h-3 w-3 mr-0.5" />
                    Verified {new Date(state.last_verified_at).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-red-700">
                    <AlertOctagon className="inline h-3 w-3 mr-0.5" />
                    Failed {new Date(state.last_verified_at).toLocaleString()}
                    {state.last_verified_detail && (
                      <span className="ml-1 opacity-70">· {state.last_verified_detail}</span>
                    )}
                  </span>
                )}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200 bg-zinc-50/60">
        <CardContent className="p-5 space-y-3">
          <div className="label-overline">How it works</div>
          <ul className="text-[12px] text-zinc-700 space-y-2 leading-relaxed list-disc pl-4">
            <li>
              Each client&apos;s <strong>Woorank tab</strong> pulls property URLs
              from the Properties tab (auto) and lets you add extra URLs
              manually.
            </li>
            <li>
              <strong>Fetch latest</strong> calls Woorank <code>GET /reviews</code>
              and stores the score + section grades + top issues.
            </li>
            <li>
              <strong>Refresh crawl</strong> triggers a new Woorank crawl
              (<code>POST /reviews</code>) — the crawl runs server-side and
              typically finishes within 60 seconds. Then click Fetch latest
              to store the new data-point.
            </li>
            <li>
              A scheduled <strong>monthly rollup</strong> repeats the fetch
              across every client + URL — head to <a href="/scheduler"
              className="text-[#00A9E0] hover:underline">/scheduler</a> to
              re-time it.
            </li>
            <li>
              Historical scores render as a trend line on the per-client
              Woorank tab so you can see SEO improving or declining.
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* ─── Woorank API capabilities catalogue ────────────────────────── */}
      <Card className="rounded-sm border-zinc-200"
        data-testid="woorank-api-catalog">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <PlugZap className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">
              Woorank API surface · what we pull vs. wishlist
            </div>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono ml-auto">
              {WOORANK_ENDPOINTS.filter((e) => e.implemented).length}
              &nbsp;/&nbsp;{WOORANK_ENDPOINTS.length} connected
            </Badge>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Every endpoint documented at{" "}
            <a href="https://apidocs.woorank.com/" target="_blank" rel="noreferrer"
              className="text-[#00A9E0] hover:underline inline-flex items-center gap-0.5">
              apidocs.woorank.com <ExternalLink className="h-2.5 w-2.5" />
            </a>
            . Toggle-ticked rows are live in Iceberg today; open rows are
            wishlist — let me know which to wire next.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left border-b border-zinc-200">
                  <th className="py-2 font-mono uppercase text-[9px] text-zinc-500 tracking-wider w-16 text-center">Live?</th>
                  <th className="py-2 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Group</th>
                  <th className="py-2 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Endpoint</th>
                  <th className="py-2 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">What it does</th>
                </tr>
              </thead>
              <tbody>
                {WOORANK_ENDPOINTS.map((e) => (
                  <tr key={`${e.method}-${e.path}`}
                    className={`border-b border-zinc-100 ${
                      e.implemented ? "bg-emerald-50/30" : "hover:bg-zinc-50"}`}
                    data-testid={`woorank-endpoint-row-${e.path.replace(/\W+/g, "-")}`}>
                    <td className="py-2 text-center">
                      {e.implemented ? (
                        <div className="inline-flex items-center justify-center h-5 w-5 rounded-sm
                          bg-emerald-600 text-white" title="Connected">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-center h-5 w-5 rounded-sm
                          border-2 border-zinc-300 bg-white" title="Wishlist / not yet connected" />
                      )}
                    </td>
                    <td className="py-2">
                      <Badge variant="outline"
                        className="rounded-sm text-[9px] font-mono">
                        {e.group}
                      </Badge>
                    </td>
                    <td className="py-2 font-mono">
                      <span className="text-[9px] font-bold text-zinc-500 mr-1">
                        {e.method}
                      </span>
                      <span className="text-zinc-800">{e.path}</span>
                      <div className="text-[10px] font-semibold text-zinc-600 mt-0.5">
                        {e.label}
                      </div>
                    </td>
                    <td className="py-2 text-[11px] text-zinc-600 leading-relaxed max-w-xl">
                      {e.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ═══ Projects & Audits — styled after Woorank's own dashboard ═══ */}
      {/* Projects panel — Woorank projects the account owns, with score,
          keywords count, pages crawled, created-on. */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden"
        data-testid="woorank-projects-card">
        <div className="bg-gradient-to-r from-violet-50 to-transparent
          px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
          <FolderKanban className="h-5 w-5 text-violet-700" />
          <div className="flex-1">
            <h2 className="font-display font-bold text-lg text-zinc-900">
              Projects
            </h2>
            <p className="text-[11px] text-zinc-500">
              Watch each site climb Google&apos;s rankings with Woorank&apos;s
              complete SEO toolset
            </p>
          </div>
          {Array.isArray(projects) && (
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
              {projects.filter((p) => p.in_woorank).length} project(s)
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={loadProjects}
            disabled={loadingProjects || !configured}
            className="rounded-sm"
            data-testid="woorank-projects-reload-btn">
            {loadingProjects
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            {projects == null ? "Load" : "Reload"}
          </Button>
        </div>

        {/* Register-orphan-project input — Woorank projects that don't
            map to a client property in Iceberg (no bulk-list on the
            Woorank API, so we can't auto-discover them). */}
        <div className="px-5 py-3 bg-violet-50/50 border-b border-zinc-100
          flex items-center gap-2 flex-wrap"
          data-testid="woorank-extra-domain-row">
          <PlugZap className="h-3.5 w-3.5 text-zinc-500" />
          <div className="text-[11px] font-semibold text-zinc-700">
            Missing a project? Register the domain:
          </div>
          <Input value={extraDomain}
            onChange={(e) => setExtraDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !extraBusy) addExtraDomain(); }}
            placeholder="example.com — bare host, no scheme"
            className="rounded-sm font-mono text-[12px] flex-1 min-w-[220px]"
            data-testid="woorank-extra-domain-input" />
          <Button size="sm" onClick={addExtraDomain}
            disabled={extraBusy || !extraDomain.trim() || !configured}
            className="rounded-sm bg-violet-700 hover:bg-violet-800 text-white"
            data-testid="woorank-add-extra-domain-btn">
            {extraBusy
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : "Register"}
          </Button>
        </div>
        <CardContent className="p-0">
          {loadingProjects && projects == null ? (
            <div className="text-center py-8 text-[12px] text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
              Pulling projects from Woorank…
            </div>
          ) : projects == null ? (
            <div className="text-center py-8 text-[12px] text-zinc-500">
              Click <strong>Load</strong> to pull project data from Woorank.
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-8 text-[12px] text-zinc-500">
              No projects found on this Woorank account yet.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              <div className="grid grid-cols-12 gap-3 px-5 py-2 bg-zinc-50
                text-[9px] font-mono uppercase tracking-wider text-zinc-500">
                <div className="col-span-1 text-center">Score</div>
                <div className="col-span-4">URL</div>
                <div className="col-span-2">Client</div>
                <div className="col-span-1 text-center">Keywords</div>
                <div className="col-span-1 text-center">Pages</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-1"></div>
              </div>
              {projects.filter((p) => p.in_woorank).map((p) => {
                const s = p.latest_score;
                const stroke = scoreColor(s);
                return (
                  <div key={p.domain}
                    className="grid grid-cols-12 gap-3 px-5 py-3 items-center
                      hover:bg-zinc-50 transition-colors"
                    data-testid={`woorank-project-row-${p.domain}`}>
                    <div className="col-span-1 flex justify-center">
                      <div className="relative h-11 w-11 flex items-center justify-center">
                        <svg width="44" height="44" viewBox="0 0 44 44"
                          className="absolute inset-0">
                          <circle cx="22" cy="22" r="19" fill="none"
                            stroke="#e4e4e7" strokeWidth="3" />
                          <circle cx="22" cy="22" r="19" fill="none"
                            stroke={stroke} strokeWidth="3"
                            strokeDasharray={`${(s || 0) * 1.194} 119.4`}
                            strokeLinecap="round"
                            transform="rotate(-90 22 22)" />
                        </svg>
                        <span className="text-[12px] font-semibold"
                          style={{ color: stroke }}>
                          {s ?? "—"}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-4 min-w-0">
                      <a href={p.url} target="_blank" rel="noreferrer"
                        className="font-semibold text-[13px] text-zinc-800
                          hover:text-[#00A9E0] inline-flex items-center gap-1">
                        {p.domain}
                        <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                      </a>
                      {(p.competitors || []).length > 0 && (
                        <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                          vs. {p.competitors.slice(0, 3).map((c) =>
                            (c || "").replace(/^https?:\/\//, "").replace(/\/$/, "")
                          ).join(", ")}
                          {p.competitors.length > 3 && ` +${p.competitors.length - 3}`}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {p.brand_primary && (
                          <span className="inline-block h-3 w-3 rounded-sm shrink-0"
                            style={{ backgroundColor: p.brand_primary }} />
                        )}
                        <span className="truncate text-[12px]">
                          {p.company_name || "—"}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-1 text-center text-[12px] text-zinc-700">
                      {p.keywords_count ?? "—"}
                    </div>
                    <div className="col-span-1 text-center text-[12px] text-zinc-700">
                      {p.pages_crawled ?? "—"}
                    </div>
                    <div className="col-span-2 text-[11px] text-zinc-500 font-mono">
                      {p.created_at
                        ? new Date(p.created_at).toLocaleDateString(undefined,
                            { day: "numeric", month: "short", year: "numeric" })
                        : "—"}
                    </div>
                    <div className="col-span-1 text-right">
                      {p.client_id && (
                        <a href={`/clients/${p.client_id}/woorank`}
                          className="text-[10px] text-[#00A9E0] hover:underline font-semibold">
                          Open →
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit list panel — every review we've ever fetched. Includes
          the "Backfill history" button (imports past scoring via
          Woorank /metrics for every known URL in one click). */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden"
        data-testid="woorank-audit-list">
        <div className="bg-gradient-to-r from-sky-50 to-transparent
          px-5 py-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <Globe2 className="h-5 w-5 text-[#00A9E0]" />
          <div className="flex-1 min-w-[200px]">
            <h2 className="font-display font-bold text-lg text-zinc-900">
              Audit
            </h2>
            <p className="text-[11px] text-zinc-500">
              Instantly check a website&apos;s SEO health and performance
            </p>
          </div>
          <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
            {reviews.length} review(s)
          </Badge>
          <Button size="sm" variant="outline" onClick={expandCompetitors}
            disabled={expanding || !configured}
            className="rounded-sm"
            title="Fetch a Woorank review for every competitor URL listed on your Woorank projects"
            data-testid="woorank-expand-competitors-btn">
            {expanding
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            Auto-expand from competitors
          </Button>
          <Button size="sm" variant="outline" onClick={runBackfill}
            disabled={backfilling || !configured}
            className="rounded-sm"
            title="Import 30 days of historical Woorank scores for every known URL"
            data-testid="woorank-backfill-btn">
            {backfilling
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <History className="h-3.5 w-3.5 mr-1" />}
            Backfill history
          </Button>
          <Button size="sm" variant="outline" onClick={loadReviews}
            disabled={loadingReviews || !configured}
            className="rounded-sm"
            data-testid="woorank-reviews-reload-btn">
            {loadingReviews
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {/* Quick-audit input — paste any URL to fetch its Woorank review */}
        <div className="px-5 py-3 bg-sky-50/50 border-b border-zinc-100
          flex items-center gap-2 flex-wrap"
          data-testid="woorank-quick-audit-row">
          <Search className="h-3.5 w-3.5 text-zinc-500" />
          <div className="text-[11px] font-semibold text-zinc-700">
            Quick audit any URL:
          </div>
          <Input value={quickUrl}
            onChange={(e) => setQuickUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !quickBusy) quickAudit(); }}
            placeholder="paste any URL to fetch its Woorank review — e.g. example.com"
            className="rounded-sm font-mono text-[12px] flex-1 min-w-[260px]"
            data-testid="woorank-quick-audit-input" />
          <Button size="sm" onClick={quickAudit}
            disabled={quickBusy || !quickUrl.trim() || !configured}
            className="rounded-sm bg-[#00A9E0] hover:bg-[#0089B8] text-white"
            data-testid="woorank-quick-audit-btn">
            {quickBusy
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : "Audit"}
          </Button>
        </div>

        <CardContent className="p-0">
          {loadingReviews && reviews.length === 0 ? (
            <div className="text-center py-8 text-[12px] text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
              Loading audits…
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-[12px] text-zinc-500">
              No audits yet. Head to a client&apos;s Woorank tab and click
              <strong> Fetch latest</strong>.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              <div className="grid grid-cols-12 gap-3 px-5 py-2 bg-zinc-50
                text-[9px] font-mono uppercase tracking-wider text-zinc-500">
                <div className="col-span-1 text-center">Score</div>
                <div className="col-span-5">URL</div>
                <div className="col-span-3">Client</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-1"></div>
              </div>
              {reviews.map((r) => {
                const s = r.score;
                const stroke = scoreColor(s);
                return (
                  <div key={r.test_id}
                    className="grid grid-cols-12 gap-3 px-5 py-3 items-center
                      hover:bg-zinc-50 transition-colors"
                    data-testid={`woorank-audit-row-${r.test_id}`}>
                    <div className="col-span-1 flex justify-center">
                      <div className="relative h-11 w-11 flex items-center justify-center">
                        <svg width="44" height="44" viewBox="0 0 44 44"
                          className="absolute inset-0">
                          <circle cx="22" cy="22" r="19" fill="none"
                            stroke="#e4e4e7" strokeWidth="3" />
                          <circle cx="22" cy="22" r="19" fill="none"
                            stroke={stroke} strokeWidth="3"
                            strokeDasharray={`${(s || 0) * 1.194} 119.4`}
                            strokeLinecap="round"
                            transform="rotate(-90 22 22)" />
                        </svg>
                        <span className="text-[12px] font-semibold"
                          style={{ color: stroke }}>
                          {s ?? "—"}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-5 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer"
                        className="font-semibold text-[13px] text-zinc-800
                          hover:text-[#00A9E0] inline-flex items-center gap-1">
                        {r.domain || r.url}
                        <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                      </a>
                      {r.runs_total > 1 && (
                        <span className="text-[10px] font-mono text-zinc-400 ml-2">
                          · {r.runs_total} runs
                        </span>
                      )}
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {r.brand_primary && (
                          <span className="inline-block h-3 w-3 rounded-sm shrink-0"
                            style={{ backgroundColor: r.brand_primary }} />
                        )}
                        <span className="truncate text-[12px]">
                          {r.company_name || r.client_id}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2 text-[11px] text-zinc-500 font-mono">
                      {r.run_at
                        ? new Date(r.run_at).toLocaleDateString(undefined,
                            { day: "numeric", month: "short", year: "numeric" })
                        : "—"}
                      {r.source === "backfill" && (
                        <Badge className="ml-1 rounded-sm text-[8px] font-mono bg-violet-100 text-violet-700">
                          BACKFILL
                        </Badge>
                      )}
                      {r.source === "competitor" && (
                        <Badge className="ml-1 rounded-sm text-[8px] font-mono bg-fuchsia-100 text-fuchsia-800">
                          COMPETITOR
                        </Badge>
                      )}
                      {r.source === "quick_audit" && (
                        <Badge className="ml-1 rounded-sm text-[8px] font-mono bg-sky-100 text-sky-800">
                          QUICK
                        </Badge>
                      )}
                      {r.source === "scheduled" && (
                        <Badge className="ml-1 rounded-sm text-[8px] font-mono bg-zinc-200 text-zinc-700">
                          SCHEDULED
                        </Badge>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      <a href={`/clients/${r.client_id}/woorank`}
                        className="text-[10px] text-[#00A9E0] hover:underline font-semibold">
                        Open →
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
